import { useEffect, useRef, useState } from "react";

export default function useScrollButton(
  processing: boolean,
  dependencies: any[],
  reset: boolean = false,
  loadingCheck?: boolean
) {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [keepAtBottom, setKeepAtBottom] = useState(false);

  // Create a ref for the scroll container
  const containerRef = useRef<HTMLDivElement>(null);
  // Create a ref for the bottom of the chat messages.
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const isAtBottomRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const processingRef = useRef(processing);

  processingRef.current = processing;
  isAtBottomRef.current = isAtBottom;

  // Attach the scroll event listener to the container when it mounts
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      // Initial check in case the container is already scrolled
      checkScrollPosition();
    }
    // Cleanup the event listener on unmount
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollPosition);
      }
    };
  }, [reset]);

  // 2. Use useEffect to scroll into view when messages or responseMessage updates.
  useEffect(() => {
    if (loadingCheck === true) return;

    if (!isAtBottomRef.current && !isFirstRender.current && !keepAtBottom) {
      return;
    }

    let isInstant = processingRef.current || isFirstRender.current;

    isFirstRender.current = false;

    if (timeoutRef.current) return;

    if (!messagesEndRef.current) {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: isInstant ? "instant" : "smooth",
      });
      return;
    }

    messagesEndRef.current?.scrollIntoView({
      behavior: isInstant ? "instant" : "smooth",
    });
  }, [...dependencies, loadingCheck]);

  // Function to check if the scroll container is near its bottom
  function checkScrollPosition() {
    const container = containerRef.current;

    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Detect scroll up: if the current scrollTop is less than the previous, fire the function
      if (scrollTop < lastScrollTopRef.current) {
        setKeepAtBottom(false);
      }

      // Update the last scroll position for future comparisons
      lastScrollTopRef.current = scrollTop;

      // If within 5 pixels of the bottom, consider it at bottom
      if (scrollTop + clientHeight >= scrollHeight - 110) {
        setIsAtBottom(true);
      } else {
        setIsAtBottom(false);
      }
    }
  }

  // Function to scroll the container to its bottom smoothly
  function scrollToBottom(behavior: "smooth" | "instant" = "smooth") {
    setKeepAtBottom(true);

    if (!messagesEndRef.current) {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: processing ? "instant" : behavior,
      });
      return;
    }

    messagesEndRef.current?.scrollIntoView({
      behavior: processing ? "instant" : behavior,
    });
  }

  return {
    keepAtBottom,
    setKeepAtBottom,
    containerRef,
    messagesEndRef,
    isAtBottom,
    scrollToBottom,
  };
}
